<div class="attachment-uploader" [class.has-drag-overlay]="dragOverlay">
  <!-- Standard drag & drop overlay, opt-in via [dragOverlay]. Markup matches
       the document manager (document-list-partial) so it reads identically to
       Task / Note / Ticket attachments, Module Documents and Jobsite Gallery.
       pointer-events are off in CSS so the drop still lands on .drop-zone. -->
  @if (dragOverlay && isDragging) {
    <div class="drag-overlay" role="region" aria-label="Drag and drop upload area">
      <div class="upload-indicator">
        @if (draggedFileCount > 0) {
          <div class="file-count-badge" aria-hidden="true">
            {{ draggedFileCount }} file{{ draggedFileCount > 1 ? 's' : '' }}
          </div>
        }
        <div class="upload-icon-circle" aria-hidden="true">
          <i class="fas fa-cloud-upload-alt upload-icon"></i>
        </div>
        <p class="upload-text">Drop {{ draggedFileCount > 0 ? draggedFileCount : '' }} file{{ draggedFileCount > 1 ? 's' : 'files' }} here to upload</p>
      </div>
    </div>
  }

  <!-- Drop Zone -->
  <div class="drop-zone"
    [class.dragging]="isDragging"
    [class.has-files]="files.length > 0"
    [class.compact]="files.length > 0"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"
    (click)="files.length === 0 ? fileInput.click() : null">

    @if (files.length === 0) {
      <div class="drop-zone-content">
        <mat-icon class="upload-icon">cloud_upload</mat-icon>
        <h3>Drag & Drop Files Here</h3>
        <p>or</p>
        <button mat-raised-button color="primary" type="button" (click)="$event.stopPropagation(); fileInput.click()">
          <mat-icon>upload</mat-icon>
          Browse Files
        </button>
        @if (showAllowedFileExtensions) {
          <p class="file-types">{{ supportedFilesText }}</p>
        }
      </div>
    }

    <!-- File List -->
    @if (files.length > 0) {
      <div class="file-list" (click)="$event.stopPropagation()">
        <div class="file-stats">
          <span>{{ files.length }} file(s) selected</span>
          <button mat-button type="button" (click)="$event.stopPropagation(); fileInput.click()" [disabled]="isUploading">
            <mat-icon>{{ !multipleUpload && hasSuccessfulFile() ? 'sync' : 'add' }}</mat-icon>
            {{ !multipleUpload && hasSuccessfulFile() ? 'Replace' : 'Add More' }}
          </button>
        </div>
        <div class="file-items">
          @for (file of files; track file; let i = $index) {
            <div class="file-item"
              [class.uploading]="file.status === 'uploading'"
              [class.success]="file.status === 'success'"
              [class.error]="file.status === 'error'"
              (click)="$event.stopPropagation()">
              <!-- Preview -->
              <div class="file-preview">
                @if (file.preview) {
                  <img [src]="file.preview" [alt]="file.file.name">
                }
                @if (!file.preview) {
                  <mat-icon class="file-icon">{{ getFileIcon(file.file.name) }}</mat-icon>
                }
              </div>
              <!-- File Info -->
              <div class="file-info">
                <div class="file-name">{{ file.file.name }}</div>
                <div class="file-size">{{ formatFileSize(file.file.size) }}</div>
                <!-- Progress Bar -->
                @if (file.status === 'uploading') {
                  <div class="file-progress">
                    <mat-progress-bar mode="determinate" [value]="file.progress"></mat-progress-bar>
                  </div>
                }
                <!-- Status -->
                <div class="file-status">
                  @if (file.status === 'pending') {
                    <span class="status-pending">
                      <mat-icon>schedule</mat-icon> Pending
                    </span>
                  }
                  @if (file.status === 'uploading') {
                    <span class="status-uploading">
                      <mat-icon>upload</mat-icon> Uploading... {{ file.progress }}%
                    </span>
                  }
                  @if (file.status === 'success') {
                    <span class="status-success">
                      <mat-icon>check_circle</mat-icon> Uploaded
                    </span>
                  }
                  @if (file.status === 'error') {
                    <span class="status-error">
                      <mat-icon>error</mat-icon> {{ file.error || 'Failed' }}
                    </span>
                  }
                </div>
              </div>
              <!-- Remove Button -->
              <button mat-icon-button
                type="button"
                class="remove-button"
                (click)="$event.stopPropagation(); removeFile(i)"
                [disabled]="isUploading && file.status === 'uploading'"
                matTooltip="Remove file">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          }
        </div>
      </div>
    }
  </div>

  <!-- Hidden File Input -->
  <input type="file"
    #fileInput
    [multiple]="multipleUpload"
    [accept]="acceptedFileTypes"
    style="display: none"
    (change)="onFileSelect($event)">

  <!-- Upload Footer (shown when manual upload is enabled) -->
  @if (files.length > 0) {
    <div class="upload-footer" (click)="$event.stopPropagation()">
      @if (isUploading) {
        <div class="upload-stats">
          <mat-spinner diameter="20"></mat-spinner>
          <span>Uploading: {{ uploadedFiles }} / {{ totalFiles }}</span>
          @if (failedFiles > 0) {
            <span class="failed-count">{{ failedFiles }} failed</span>
          }
        </div>
      }
      @if (manualUpload) {
        <div class="upload-actions">
          <button mat-button type="button" (click)="$event.stopPropagation(); clearAllFiles()" [disabled]="isUploading">
            Clear All
          </button>
          <button mat-raised-button
            type="button"
            color="primary"
            (click)="$event.stopPropagation(); uploadFiles()"
            [disabled]="files.length === 0 || isUploading || allFilesUploaded()">
            <mat-icon>cloud_upload</mat-icon>
            {{ isUploading ? 'Uploading...' : 'Upload All' }}
          </button>
        </div>
      }
    </div>
  }

  <!-- Validation Error -->
  <app-validation-error [formGroup]="sectionForm" [controlName]="'file'"></app-validation-error>
</div>
